/**
 * Reports & Analytics Page
 * =========================
 * صفحة التقارير والإحصائيات
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import SAR from '../../../components/SAR';

const I = {
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  trending: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  orders: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>,
  revenue: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
};

const StatBox = ({ icon, label, value, change, color }) => (
  <div className="card" style={{ padding: '20px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--tenant-text)' }}>{value}</div>
        <div style={{ fontSize: '13px', color: 'var(--tenant-text-muted)' }}>{label}</div>
      </div>
      {change && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '500', color: change > 0 ? 'var(--tenant-success)' : 'var(--tenant-danger)', background: change > 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '20px' }}>
          {I.trending}
          <span>{change > 0 ? '+' : ''}{change}%</span>
        </div>
      )}
    </div>
  </div>
);

const ReportsPage = () => {
  const { apiCall } = useTenantAuth();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topDrivers, setTopDrivers] = useState([]);
  const [topShips, setTopShips] = useState([]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, dailyStats, driversData, shipsData] = await Promise.all([
        apiCall(`/tenant/dashboard/summary?range=${dateRange}`),
        apiCall(`/tenant/dashboard/daily-stats?range=${dateRange}`),
        apiCall('/tenant/drivers?sort=orders_desc&limit=5'),
        apiCall('/tenant/ships?sort=orders_desc&limit=5')
      ]);
      
      setStats(statsData);
      setDailyData(dailyStats?.items || []);
      setTopDrivers(driversData?.items || []);
      setTopShips(shipsData?.items || []);
    } catch (err) {
      showError('فشل تحميل التقارير');
    } finally {
      setLoading(false);
    }
  }, [apiCall, dateRange, showError]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const exportReport = async (format) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1'}/tenant/reports/export?format=${format}&range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('watrly_tenant_token')}` }
      });
      
      if (!response.ok) throw new Error('فشل تصدير التقرير');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(err.message);
    }
  };

  const maxOrders = Math.max(...dailyData.map(d => d.orders || 0), 1);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>التقارير والإحصائيات</h1>
          <p>تحليلات أداء الشركة</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="filter-select">
            <option value="week">هذا الأسبوع</option>
            <option value="month">هذا الشهر</option>
            <option value="quarter">هذا الربع</option>
            <option value="year">هذا العام</option>
          </select>
          <button className="btn btn--secondary" onClick={loadReports}>{I.refresh}</button>
          <button className="btn btn--primary" onClick={() => exportReport('xlsx')}>
            {I.download} <span>تصدير</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <>
          {/* Stats Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <StatBox icon={I.orders} label="إجمالي الطلبات" value={stats?.orders?.total || 0} change={12} color="var(--tenant-primary)" />
            <StatBox icon={I.revenue} label="الإيرادات" value={<>{(stats?.revenue?.total || 0).toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></>} change={8} color="var(--tenant-success)" />
            <StatBox icon={I.users} label="العملاء الجدد" value={stats?.customers?.new || 0} change={-3} color="#8b5cf6" />
            <StatBox icon={I.trending} label="متوسط قيمة الطلب" value={<>{(stats?.orders?.avg_value || 0).toFixed(0)}{' '}<SAR className="h-[0.8em] w-auto" /></>} color="var(--tenant-warning)" />
          </div>

          {/* Daily Chart */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">الطلبات اليومية</div>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '200px' }}>
                {dailyData.length > 0 ? dailyData.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '100%', maxWidth: '40px', height: `${(d.orders / maxOrders) * 160}px`, minHeight: '4px', background: 'linear-gradient(to top, var(--tenant-primary), var(--tenant-primary-dark))', borderRadius: '4px 4px 0 0' }} />
                    <span style={{ fontSize: '10px', color: 'var(--tenant-text-muted)' }}>{d.day}</span>
                  </div>
                )) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tenant-text-muted)' }}>لا توجد بيانات</div>
                )}
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="card">
              <div className="card-header">
                <div className="card-title">أفضل السواقين</div>
              </div>
              <div style={{ padding: '0' }}>
                {topDrivers.length > 0 ? topDrivers.map((d, i) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderBottom: i < topDrivers.length - 1 ? '1px solid var(--tenant-border)' : 'none' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'var(--tenant-bg)', color: i < 3 ? 'white' : 'var(--tenant-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600' }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{d.full_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--tenant-text-muted)' }}>{d.total_orders || 0} طلب</div>
                    </div>
                    <div style={{ fontWeight: '600', color: 'var(--tenant-primary)' }}>{d.rating_avg?.toFixed(1) || '-'} ⭐</div>
                  </div>
                )) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--tenant-text-muted)' }}>لا توجد بيانات</div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">أفضل الأشياب</div>
              </div>
              <div style={{ padding: '0' }}>
                {topShips.length > 0 ? topShips.map((s, i) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderBottom: i < topShips.length - 1 ? '1px solid var(--tenant-border)' : 'none' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'var(--tenant-bg)', color: i < 3 ? 'white' : 'var(--tenant-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600' }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{s.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--tenant-text-muted)' }}>{s.total_orders || 0} طلب</div>
                    </div>
                    <div style={{ fontWeight: '600', color: 'var(--tenant-primary)' }}>{s.rating_avg?.toFixed(1) || '-'} ⭐</div>
                  </div>
                )) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--tenant-text-muted)' }}>لا توجد بيانات</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
